import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiFilterOp } from '@euler/model/bi/query';
import { BiFieldRole } from '@euler/model/bi/result';
import { Plus } from 'lucide-react-native';
import { memo, useState } from 'react';
import { TextInput, View } from 'react-native';
import { BiButton } from '../../components/ui/BiButton';
import { BiChip } from '../../components/ui/BiChip';
import { EditorSection } from './EditorSection';
import { biFilterLabel, kBiFilterOpLabels } from './functions/biEditorLabels';
import { BiEditorQuery } from './functions/biEditorState';
import {
  biCodedFilterFromPick,
  biFilterFromForm,
  biFilterOpsFor,
  biTogglePickedCode,
  isBiCodedField,
} from './functions/biFilterDraft';

/** Row filters: the ones the card has, and a form to add one. */
export const FiltersSection = memo(
  ({
    query,
    catalog,
    onChange,
  }: {
    query: BiEditorQuery;
    catalog: BiDatasetCatalog | null;
    onChange: (next: BiEditorQuery) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const fields =
      catalog?.fields.filter(field => field.role === BiFieldRole.Dimension) ??
      [];
    const [fieldKey, setFieldKey] = useState<string | null>(null);
    const [op, setOp] = useState<BiFilterOp>(BiFilterOp.Eq);
    const [text, setText] = useState('');
    const [picked, setPicked] = useState<string[]>([]);
    const field = fields.find(entry => entry.key === fieldKey) ?? null;
    const coded = field ? isBiCodedField(field) : false;
    const ops = field ? biFilterOpsFor(field) : [];
    const draft = !field
      ? null
      : coded
      ? biCodedFilterFromPick(field, op, picked)
      : biFilterFromForm(field, op, text);
    const add = () => {
      if (!draft) return;
      onChange({ ...query, filters: [...query.filters, draft] });
      setText('');
      setPicked([]);
      setFieldKey(null);
    };
    const pickOp = (value: BiFilterOp) => {
      setOp(value);
      // 等于 / 不等于 take one value: keep the first picked.
      if (value === BiFilterOp.Eq || value === BiFilterOp.Neq)
        setPicked(codes => codes.slice(0, 1));
    };
    return (
      <EditorSection title="筛选">
        {query.filters.length ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
            {query.filters.map((filter, index) => (
              <BiChip
                key={`${filter.field}:${index}`}
                label={`${biFilterLabel(catalog, filter)} ×`}
                selected
                onPress={() =>
                  onChange({
                    ...query,
                    filters: query.filters.filter((_, i) => i !== index),
                  })
                }
              />
            ))}
          </View>
        ) : null}
        {fields.length ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
            {fields.map(entry => (
              <BiChip
                key={entry.key}
                icon={entry.key === fieldKey ? undefined : Plus}
                label={biText(entry.label)}
                selected={entry.key === fieldKey}
                onPress={() => {
                  setFieldKey(entry.key === fieldKey ? null : entry.key);
                  setOp(biFilterOpsFor(entry)[0]);
                  setPicked([]);
                }}
              />
            ))}
          </View>
        ) : null}
        {field ? (
          <View style={{ gap: rpx(6) }}>
            <View
              style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}
            >
              {ops.map(value => (
                <BiChip
                  key={value}
                  label={kBiFilterOpLabels[value]}
                  selected={op === value}
                  onPress={() => pickOp(value)}
                />
              ))}
            </View>
            {coded ? (
              <View
                style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}
              >
                {Object.entries(field.valueLabels ?? {}).map(
                  ([code, label]) => (
                    <BiChip
                      key={code}
                      label={label}
                      selected={picked.includes(code)}
                      onPress={() =>
                        setPicked(codes => biTogglePickedCode(op, codes, code))
                      }
                    />
                  ),
                )}
                <BiButton title="添加" disabled={!draft} onPress={add} />
              </View>
            ) : (
              <View style={{ flexDirection: 'row', gap: rpx(8) }}>
                <TextInput
                  accessibilityLabel="筛选值"
                  value={text}
                  onChangeText={setText}
                  onSubmitEditing={add}
                  placeholder={
                    op === BiFilterOp.In || op === BiFilterOp.NotIn
                      ? '多个值用顿号或逗号分隔'
                      : '筛选值'
                  }
                  placeholderTextColor={aiColors.mutedForeground}
                  style={{
                    flex: 1,
                    height: rpx(34),
                    paddingHorizontal: rpx(10),
                    borderRadius: rpx(10),
                    backgroundColor: aiColors.input,
                    color: aiColors.foreground,
                    fontSize: rpx(13),
                  }}
                />
                <BiButton title="添加" disabled={!draft} onPress={add} />
              </View>
            )}
          </View>
        ) : null}
      </EditorSection>
    );
  },
);
